import { FilterList } from '@mui/icons-material';
import { Grid, IconButton } from '@mui/material';
import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { TableInstance } from 'react-table';
import { ExportButton } from 'shared/components/export-button';
import { SecondaryActionButton } from 'shared/components/secondary-button';
import { PERMISSIONS } from 'shared/constants';
import { useQuoteListState } from 'shared/contexts/quotes-summary-context';
import { useUserPermissions } from 'shared/hooks/use-user-permissions';
import { useExportQuotesSummary } from 'shared/mutations/export-quotes-summary';
import { useAuthState } from 'shared/stores';
import { QuotesSummaryItem } from 'shared/types';
import { QuoteTableFilters } from './quote-columns-filters';
import { QuoteSummaryStatusFilter } from './quote-summary-status-filter';
import { QuoteTableFilterChipBar } from './quote-table-filter-chip-bar';

interface Props {
  quotesSummaryTableInstance: TableInstance<QuotesSummaryItem>;
}

export function QuotesSummaryFilter({ quotesSummaryTableInstance }: Props) {
  const authState = useAuthState();
  const userEmail = authState.user.value?.email ?? '';
  const userId = authState.user.value?.id ?? '';
  const userPermissions = useUserPermissions();
  const userIsAdmin = userPermissions.includes(PERMISSIONS['quoting.admin']);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const history = useHistory();
  const { quoteTableState } = useQuoteListState();
  const quotesSummaryExportMutation = useExportQuotesSummary({
    search: quoteTableState.searchFilters,
    userEmail,
    userIsAdmin,
    userId,
  });

  const openFilters = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
      setFilterOpen(true);
    },
    [setAnchorEl, setFilterOpen]
  );

  const handlePopoverClose = useCallback(() => {
    setFilterOpen(false);
    setAnchorEl(null);
  }, []);

  const handleNewQuoteClicked = () => {
    history.push('/quoting/add');
  };

  const exportQuotesSummary = useCallback(() => {
    quotesSummaryExportMutation.mutate();
  }, [quotesSummaryExportMutation]);

  return (
    <>
      <Grid container justifyContent='space-between' alignItems='center' direction='row' spacing={1}>
        <Grid container xs={11} justifyContent='space-between' direction='row'>
          <Grid item xs={9}>
            <SecondaryActionButton actionText='Add New Quote Request' onClick={handleNewQuoteClicked} />
          </Grid>
          <Grid item xs={3}>
            <QuoteSummaryStatusFilter quotesSummaryTableInstance={quotesSummaryTableInstance} />
          </Grid>
        </Grid>
        <Grid container xs={0.5} display='flex' justifyContent='flex-end'>
          <IconButton onClick={openFilters}>
            <FilterList />
          </IconButton>
          <QuoteTableFilters
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            show={filterOpen}
            quotesSummaryTableInstance={quotesSummaryTableInstance}
          />
        </Grid>
        <Grid item xs={0.5}>
          <ExportButton noun='Quote Table' onClick={exportQuotesSummary} />
        </Grid>
      </Grid>
      <Grid container justifyContent='flex-end' alignItems='center' direction='row' spacing={1}>
        <Grid item xs={12}>
          <QuoteTableFilterChipBar />
        </Grid>
      </Grid>
    </>
  );
}
